import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { adminApi, reservationApi } from '../../services/api';
import { 
  FiCalendar, 
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiUser,
  FiClock 
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { format, differenceInMinutes } from 'date-fns';

function AdminReservationsPage() {
  const { user } = useAuthStore();
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [userFilter, setUserFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReservations, setTotalReservations] = useState(0);
  const [paginationLimit] = useState(10);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showReservationDetailModal, setShowReservationDetailModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [allUsers, setAllUsers] = useState([]);

  // Fetch reservations
  const fetchReservations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Calculate date range
      let startDate, endDate;
      const now = new Date();
      
      switch (dateRange) {
        case 'today':
          startDate = format(now, 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
          break;
        case 'week':
          startDate = format(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
          break;
        case 'month':
          startDate = format(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
          break;
        case 'future':
          startDate = format(now, 'yyyy-MM-dd');
          endDate = format(new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
          break;
        case 'past':
          startDate = format(new Date('2020-01-01'), 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
          break;
        default:
          startDate = undefined;
          endDate = undefined;
      }
      
      const params = {
        page: currentPage,
        limit: paginationLimit,
        search: searchQuery || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        startDate: startDate,
        endDate: endDate,
        userId: userFilter || undefined
      };
      
      // Remove undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) delete params[key];
      });
      
      const response = await adminApi.getAllReservations(params);
      
      setReservations(response.data.reservations || []);
      setTotalReservations(response.data.pagination?.total || 0);
      setTotalPages(response.data.pagination?.totalPages || 1);
      
      // Fetch all users for filter
      if (allUsers.length === 0) {
        const usersResponse = await adminApi.getUsers({ limit: 1000 });
        setAllUsers(usersResponse.data.users || []);
      }
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError('Nepodařilo se načíst rezervace. Zkuste to prosím znovu.');
      toast.error('Chyba při načítání rezervací');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter, dateRange, userFilter, paginationLimit, allUsers.length]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchReservations();
  };

  // Handle filter change
  const handleFilterChange = (filter, value) => {
    switch (filter) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'dateRange':
        setDateRange(value);
        break;
      case 'user':
        setUserFilter(value);
        break;
    }
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Get status color
  const getStatusColor = (isCancelled) => {
    return isCancelled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800';
  };

  // Get status label
  const getStatusLabel = (isCancelled) => {
    return isCancelled ? 'Zrušeno' : 'Aktivní';
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  // Format short date
  const formatShortDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd.MM. HH:mm');
    } catch {
      return dateString;
    }
  };

  // Get duration
  const getDuration = (startTime, endTime) => {
    try {
      const duration = differenceInMinutes(new Date(endTime), new Date(startTime));
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      
      if (hours === 0) {
        return `${minutes} min`;
      } else if (minutes === 0) {
        return `${hours} h`;
      } else {
        return `${hours} h ${minutes} min`;
      }
    } catch {
      return 'N/A';
    }
  };

  // Delete reservation
  const handleDeleteReservation = async (reservationId) => {
    try {
      await reservationApi.delete(reservationId);
      toast.success('Rezervace zrušena');
      fetchReservations();
    } catch (err) {
      console.error('Error deleting reservation:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se zrušit rezervaci';
      toast.error(message);
    }
  };

  // Open confirm modal
  const openConfirmModal = (action, reservationId) => {
    setModalAction(action);
    setSelectedReservation(reservations.find(r => r.id === reservationId));
    setShowConfirmModal(true);
  };

  // Execute action from modal
  const executeAction = async () => {
    if (!selectedReservation || !modalAction) return;
    
    if (modalAction === 'delete') {
      await handleDeleteReservation(selectedReservation.id);
    }
    
    setShowConfirmModal(false);
    setSelectedReservation(null);
    setModalAction('');
  };

  // Get user name by ID
  const getUserName = (userId) => {
    if (!userId) return 'Neznámý';
    const user = allUsers.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : userId;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Správa rezervací</h1>
        <p className="text-sm text-gray-600 mt-1">
          Seznam všech rezervací v systému
        </p>
      </div>

      {/* Loading state */}
      {isLoading && currentPage === 1 ? (
        <div className="flex items-center justify-center py-20">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center">
          <FiAlertCircle className="mr-2" />
          {error}
        </div>
      ) : (
        <>
          {/* Search and Filter */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hledat podle názvu
                  </label>
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Hledat podle názvu rezervace"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filtr podle uživatele
                  </label>
                  <div className="relative">
                    <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <select
                      value={userFilter}
                      onChange={(e) => handleFilterChange('user', e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Všichni uživatelé</option>
                      {allUsers.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.firstName} {u.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stav
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="all">Všechny rezervace</option>
                    <option value="active">Aktivní</option>
                    <option value="cancelled">Zrušené</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Období
                  </label>
                  <select
                    value={dateRange}
                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="all">Všechny</option>
                    <option value="today">Dnes</option>
                    <option value="week">Poslední týden</option>
                    <option value="month">Poslední měsíc</option>
                    <option value="future">Budoucí</option>
                    <option value="past">Minulé</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors"
                >
                  <FiSearch className="mr-2" />
                  Hledat
                </button>
              </div>
            </form>
          </div>

          {/* Results count */}
          <div className="bg-white rounded-xl shadow-md p-4 flex items-center justify-between">
            <div className="flex items-center">
              <FiCalendar className="text-blue-600 mr-2" />
              <span className="text-gray-900">
                <strong>{totalReservations}</strong> rezervací nalezeno
              </span>
            </div>
          </div>

          {/* Reservations Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Název
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uživatel
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Datum a čas
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trvání
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stav
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Akce
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reservations.map(reservation => (
                    <tr key={reservation.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900 truncate max-w-xs">
                          {reservation.title}
                        </div>
                        {reservation.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {reservation.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {reservation.user?.firstName} {reservation.user?.lastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-900">
                          {formatShortDate(reservation.startTime)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatShortDate(reservation.endTime)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {getDuration(reservation.startTime, reservation.endTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation.isCancelled)}`}>
                          {getStatusLabel(reservation.isCancelled)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setSelectedReservation(reservation);
                              setShowReservationDetailModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 transition-colors p-1"
                            title="Zobrazit detail"
                          >
                            <FiEye />
                          </button>
                          {!reservation.isCancelled && (
                            <button
                              onClick={() => openConfirmModal('delete', reservation.id)}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                              title="Zrušit rezervaci"
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile view */}
            <div className="lg:hidden">
              <div className="divide-y divide-gray-200">
                {reservations.map(reservation => (
                  <div key={reservation.id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-gray-900 truncate">{reservation.title}</h3>
                        <p className="text-sm text-gray-500">
                          {reservation.user?.firstName} {reservation.user?.lastName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatShortDate(reservation.startTime)} - {formatShortDate(reservation.endTime)}
                        </p>
                        <p className="text-sm text-gray-500">
                          Trvání: {getDuration(reservation.startTime, reservation.endTime)}
                        </p>
                        <span className={`inline-block mt-1 px-2 py-1 rounded text-xs font-medium ${getStatusColor(reservation.isCancelled)}`}>
                          {getStatusLabel(reservation.isCancelled)}
                        </span>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setShowReservationDetailModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Zobrazit"
                        >
                          <FiEye />
                        </button>
                        {!reservation.isCancelled && (
                          <button
                            onClick={() => openConfirmModal('delete', reservation.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Zrušit"
                          >
                            <FiTrash2 />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-xl shadow-md p-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-gray-600">
                  Zobrazuji {((currentPage - 1) * paginationLimit) + 1} - {Math.min(currentPage * paginationLimit, totalReservations)} z {totalReservations}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <FiChevronLeft className="text-gray-600" />
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page = currentPage - 2 + i;
                    if (page < 1) page = 1;
                    if (page > totalPages) page = totalPages;
                    
                    // Skip duplicates
                    if (i > 0 && page === currentPage - 2 + (i - 1)) return null;
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                          currentPage === page 
                            ? 'bg-blue-600 text-white' 
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <FiChevronRight className="text-gray-600" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Reservation Detail Modal */}
      {showReservationDetailModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Detail rezervace</h2>
              <button
                onClick={() => setShowReservationDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <h3 className="font-medium text-gray-900">{selectedReservation.title}</h3>
                {selectedReservation.description && (
                  <p className="text-gray-600 mt-2">{selectedReservation.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Uživatel</div>
                  <div className="font-medium text-gray-900">
                    {getUserName(selectedReservation.userId)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Email</div>
                  <div className="font-medium text-gray-900">
                    {selectedReservation.user?.email}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Začátek</div>
                  <div className="font-medium text-gray-900">
                    {formatDate(selectedReservation.startTime)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Konec</div>
                  <div className="font-medium text-gray-900">
                    {formatDate(selectedReservation.endTime)}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500">Trvání</div>
                  <div className="font-medium text-gray-900">
                    {getDuration(selectedReservation.startTime, selectedReservation.endTime)}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500">Stav</div>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedReservation.isCancelled)}`}>
                    {getStatusLabel(selectedReservation.isCancelled)}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowReservationDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                >
                  Zavřít
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <FiAlertCircle className="w-6 h-6 text-red-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {modalAction === 'delete' && 'Zrušit rezervaci'}
                  </h3>
                  <div className="mt-2 text-sm text-gray-500">
                    <p>
                      {modalAction === 'delete' && `Opravdu chcete zrušit rezervaci "${selectedReservation.title}"?`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-2 rounded-b-xl">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedReservation(null);
                  setModalAction('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
              >
                Zrušit
              </button>
              <button
                onClick={executeAction}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 transition-colors"
              >
                <FiCheckCircle className="mr-2" />
                Potvrdit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminReservationsPage;
